import { useRef } from 'react';
import { CSSObject, Global } from '@emotion/react';
import { Box, useTokens } from '@gemini/core';
import { MinusIcon, PlusIcon } from '@gemini/icons';
import type { Tokens } from '@gemini/tokens';
import { StateMessage } from '@gemini/ui';
import Popover from '@mfe-presentation/components/Popover';
import { TStyleProps } from '@mfe-types/box';
import { IFieldOptions, IFieldProps } from '@mfe-types/field';

type TIncrementalWrapperOptions = Required<
    Pick<
        IFieldOptions,
        | 'decrementFn'
        | 'incrementFn'
        | 'matchLabelFromSelectionIfExists'
        | 'matchValueFromSelectionIfExists'
        | 'matchValueFromManualInputIfExists'
    >
>;

const incrementalButtonStyles: TStyleProps = {
    borderWidth: 'borderWidth.none',
    paddingHorizontal: 'spacing.12',
    paddingVertical: 'spacing.12',
    cursor: 'pointer',
    backgroundColor: 'color.surface.light.default',
    color: 'color.content.default.default',
    borderRadius: 'radius.4',
    padding: 'spacing.4',
    marginHorizontal: 'spacing.8',
};

const incrementalButtonAdditionalStyles = (tokens: Tokens): CSSObject => ({
    '.incremental-button': {
        '&:hover': {
            backgroundColor: tokens['color.surface.interactive.hover'],
        },
        '&:active': {
            backgroundColor: tokens['color.surface.interactive.pressed'],
        },
    },
});

const IncrementalWrapper = ({
    field,
    options,
    texts,
    styles,
}: Required<IFieldProps<any, any, TIncrementalWrapperOptions>>) => {
    const inputRef = useRef<HTMLInputElement>(null);

    const tokens = useTokens();

    const decrement = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        let newValue =
            typeof options.matchValueFromSelectionIfExists === 'function' && texts.customValues
                ? options.matchValueFromSelectionIfExists(field.value, texts.customValues)
                : field.value;
        newValue = options.decrementFn(newValue, field.increment);
        if (newValue) field.onChange(newValue);
    };

    const increment = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.preventDefault();
        const newValue =
            typeof options.matchValueFromSelectionIfExists === 'function' && texts.customValues
                ? options.matchValueFromSelectionIfExists(field.value, texts.customValues)
                : field.value;
        field.onChange(options.incrementFn(newValue, field.increment));
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        const newValue =
            typeof options.matchValueFromSelectionIfExists === 'function' && texts.customValues
                ? `${options.matchValueFromSelectionIfExists(e.target.value, texts.customValues)}`
                : e.target.value;
        field.onBlur(newValue);
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue =
            typeof options.matchValueFromManualInputIfExists === 'function' && texts.customValues
                ? options.matchValueFromManualInputIfExists(e.target.value, texts.customValues)
                : e.target.value;
        field.onChange(newValue);
    };

    const handleClick = () => {
        inputRef?.current?.select();
    };

    const value =
        typeof options.matchLabelFromSelectionIfExists === 'function' && texts.customValues
            ? options.matchLabelFromSelectionIfExists(field.value, texts.customValues)
            : field.value;

    return (
        <Box {...styles} testId={`${field.name}Container`}>
            {texts.tooltip ? (
                <Popover
                    name={`${field.name}Popover`}
                    label={texts.label}
                    iconButtonAriaDescribedby={`${field.name}-description`}
                >
                    <Box id={`${field.name}-description`} typography="typography.body.14.regular">
                        {texts.tooltip}
                    </Box>
                </Popover>
            ) : (
                <Box
                    as="label"
                    typography="typography.body.14.bold"
                    paddingVertical="spacing.4"
                    marginVertical="spacing.none"
                    color="color.content.default.default"
                >
                    {texts.label}
                </Box>
            )}
            <Box
                display="flex"
                flexDirection="row"
                justifyContent="space-between"
                alignItems="center"
                width="100%"
                borderWidth="borderWidth.1"
                borderStyle="solid"
                borderColor="color.border.subdued.default"
                borderRadius="radius.8"
                color="color.content.light.default"
                padding="spacing.none"
                height="48px"
            >
                <Global styles={() => incrementalButtonAdditionalStyles(tokens)} />
                <Box
                    as="button"
                    aria-label={texts?.ariaLabel?.minus}
                    testId={`${field.name}DecrementButton`}
                    {...incrementalButtonStyles}
                    unsafe_className="incremental-button"
                    onClick={decrement}
                    outlineColor="color.border.interactive.default"
                >
                    <MinusIcon />
                </Box>
                <Box
                    as="input"
                    type={field.type}
                    inputMode={field.inputMode}
                    borderWidth="borderWidth.none"
                    paddingHorizontal="spacing.12"
                    paddingVertical="spacing.12"
                    textAlign="center"
                    width="100%"
                    outlineWidth="borderWidth.none"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    onClick={handleClick}
                    ref={inputRef}
                    name={field.name}
                    value={value}
                    aria-label={texts?.label}
                    aria-required={!field.isOptional}
                />
                <Box
                    as="button"
                    aria-label={texts?.ariaLabel?.plus}
                    testId={`${field.name}IncrementButton`}
                    {...incrementalButtonStyles}
                    unsafe_className="incremental-button"
                    onClick={increment}
                    outlineColor="color.border.interactive.default"
                >
                    <PlusIcon />
                </Box>
            </Box>
            {/* An error will be displayed when copy pasting non-numeric characters */}
            {field.error && (
                <StateMessage marginVertical="spacing.4" variant="error">
                    {field.error}
                </StateMessage>
            )}
        </Box>
    );
};

export default IncrementalWrapper;
